// Parser for Instagram (Meta) warrant returns: the records.html file Meta sends, made of
// <div id="property-…"> sections. Plain JavaScript, no React or DOM: used by the Instagram
// Warrant Return Viewer page and by the Case Analyzer desktop app.

// ─── Section metadata ─────────────────────────────────────────────────────────

export const SECTION_KEYS = [
  'request_parameters','ncmec_reports','name','emails','vanity','vanity_changes',
  'registration_date','registration_ip','account_end_date',
  'phone_numbers','ip_addresses','searches','privacy_settings','privacy_blocks',
  'popular_block','devices','gender','date_of_birth','website',
  'following','followers','incoming_follow_requests','about_me',
  'photos','profile_picture','comments','videos','live_videos','archived_live_videos',
  'notes','unified_messages','likes','comment_likes',
  'reported_conversations','reported_disappearing_messages',
  'archived_stories','saved_media',
  'encrypted_connection_info','encrypted_groups_info',
  'threads_about_me','threads_profile_picture','threads_following','threads_followers',
  'threads_liked_posts','threads_registration_date','threads_privacy_settings',
  'threads_account_searches','threads_keyword_searches','threads_muted_users',
  'threads_account_favorited','threads_account_unfollowed','threads_incoming_follow_requests',
  'threads_posts_and_replies','threads_archived_stories','unarchived_stories',
];

export const SECTION_LABELS = Object.fromEntries(
  SECTION_KEYS.map(k => [k, k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())])
);

export const MESSAGE_SECTION_KEYS = [
  'unified_messages','reported_conversations','reported_disappearing_messages',
];
export const MESSAGE_SECTIONS = new Set(MESSAGE_SECTION_KEYS);
export const IP_KEYS = new Set(['ip_addresses']);
export const MEDIA_SECTION_KEYS = new Set(['photos','videos','live_videos','archived_live_videos','profile_picture']);
export const FOLLOWER_SECTION_KEYS = new Set(['followers','following','incoming_follow_requests']);
export const COMMENT_SECTION_KEYS = new Set(['comments','comment_likes']);
export const LIKES_SECTION_KEYS = new Set(['likes']);
export const DEVICES_SECTION_KEYS = new Set(['devices']);

// ─── Parser helpers ───────────────────────────────────────────────────────────

function htmlClean(raw) {
  return raw
    .replace(/<[^>]+>/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(+n))
    .replace(/\s+/g, ' ')
    .trim();
}

function sliceSections(html) {
  const re = /<div\s+id="(property-[^"]+)"/g;
  const matches = [];
  let m;
  while ((m = re.exec(html)) !== null)
    matches.push({ key: m[1].replace('property-', ''), index: m.index });
  const out = {};
  for (let i = 0; i < matches.length; i++) {
    const { key, index } = matches[i];
    const end = i + 1 < matches.length ? matches[i + 1].index : html.length;
    out[key] = html.slice(index, end);
  }
  return out;
}

function fieldValue(html, label) {
  const target = label.toLowerCase();
  const re = />([A-Za-z][^<\n]{1,80}?)<div class="m"><div>([^<]+)/g;
  let m;
  while ((m = re.exec(html)) !== null) {
    if (m[1].trim().toLowerCase() === target) {
      const v = htmlClean(m[2]);
      if (v && !v.toLowerCase().includes('no responsive records located')) return v;
    }
  }
  return '';
}

function extractName(html) {
  return [fieldValue(html, 'first'), fieldValue(html, 'middle'), fieldValue(html, 'last')]
    .filter(Boolean).join(' ');
}

const COUNT_HINTS = {
  ip_addresses:               '>IP Address<',
  unified_messages:           '>Thread<',
  reported_conversations:     '>Thread<',
  reported_disappearing_messages: '>Thread<',
  followers:                  '>Follower<',
  following:                  '>Following<',
  incoming_follow_requests:   '>Follow Request<',
  photos:                     '<img ',
  profile_picture:            '<img ',
  videos:                     '<video ',
  live_videos:                '<video ',
  archived_live_videos:       '<video ',
  comments:                   '>Date Created<',
  comment_likes:              '>Date Created<',
  likes:                      '>Like Timestamp<',
  devices:                    '>Active<',
};

function countSection(key, html) {
  const hint = COUNT_HINTS[key];
  if (hint) {
    let count = 0, pos = 0;
    while ((pos = html.indexOf(hint, pos)) !== -1) { count++; pos += hint.length; }
    return count;
  }
  const re = />([A-Za-z][^<\n]{1,80}?)<div class="m"><div>([^<]+)/g;
  let m;
  while ((m = re.exec(html)) !== null) {
    const lbl = m[1].trim();
    if (lbl.endsWith('Definition')) continue;
    const val = htmlClean(m[2]);
    if (val && !val.toLowerCase().includes('no responsive records located')) return 1;
  }
  return 0;
}

function extractKvPairs(html) {
  const re = />([A-Za-z][^<\n]{1,80}?)<div class="m"><div>([^<]+)/g;
  const out = [];
  let m;
  while ((m = re.exec(html)) !== null) {
    const lbl = m[1].trim();
    if (!lbl || lbl.endsWith('Definition')) continue;
    const val = htmlClean(m[2]);
    if (!val || val.toLowerCase().includes('no responsive records located')) continue;
    out.push({ label: lbl, value: val });
  }
  const seen = new Set();
  let record = 0;
  for (const row of out) {
    if (seen.has(row.label)) { record++; seen.clear(); }
    seen.add(row.label);
    row.record = record;
  }
  return out;
}

function extractIpRecords(html) {
  const re1 = />IP Address<div class="m"><div>([^<]+)/g;
  const positions = [];
  let m;
  while ((m = re1.exec(html)) !== null)
    positions.push({ ip: m[1].trim(), index: m.index, end: m.index + m[0].length });
  if (positions.length > 0) {
    const records = [];
    for (let i = 0; i < positions.length; i++) {
      const block = html.slice(positions[i].end,
        i + 1 < positions.length ? positions[i + 1].index : html.length);
      const timeM   = block.match(/>Time<div class="m"><div>([^<]+)/);
      const actionM = block.match(/>Action<div class="m"><div>([^<]+)/);
      if (!timeM) continue;
      const agentM  = block.match(/>Agent String<div class="m"><div>([^<]+)/);
      records.push({
        ip:     positions[i].ip,
        time:   timeM[1].trim(),
        action: actionM ? actionM[1].trim() : '',
        agent:  agentM  ? agentM[1].trim()  : '',
      });
    }
    if (records.length > 0) return records;
  }
  return [];
}

function parseParticipants(chunk) {
  const m = chunk.match(
    /Current Participants<div class="m"><div>([\s\S]*?)(?=<div class="t [oi]">(?!<div class="m">)|<div id="page_)/
  );
  if (!m) return [];
  return m[1].split(/<div class="p">/).map(htmlClean).filter(p => {
    if (!p) return false;
    if (/^\d{4}-\d{2}-\d{2}/.test(p)) return false;
    if (p === 'Group Profile Picture' || p === 'Linked Media File') return false;
    return true;
  });
}

function parseThreadMessages(chunk) {
  const re = />(Author|Sent|Body|Removed by Sender)<div class="m"><div>([\s\S]*?)(?=<div class="p">)/g;
  const messages = [];
  let current = null;
  let m;
  while ((m = re.exec(chunk)) !== null) {
    const label = m[1];
    const value = htmlClean(m[2]);
    if (label === 'Author') {
      if (current) messages.push(current);
      current = { author: value, sent: '', body: '', removed: false };
    } else if (current) {
      if (label === 'Sent')               current.sent   = value;
      else if (label === 'Body')          current.body   = value;
      else if (label === 'Removed by Sender') current.removed = true;
    }
  }
  if (current) messages.push(current);
  return messages;
}

function parseMessageThreads(html) {
  const re = /<div class="t i">Thread<div class="m"><div>\s*\((\d+)\)/g;
  const markers = [];
  let m;
  while ((m = re.exec(html)) !== null) markers.push({ tid: m[1], index: m.index });
  if (!markers.length) return [];
  return markers.map((marker, i) => {
    const chunk = html.slice(marker.index, i + 1 < markers.length ? markers[i + 1].index : html.length);
    const nameM = chunk.match(/Thread Name<div class="m"><div>([^<]+)/);
    return {
      thread_id:    marker.tid,
      thread_name:  nameM ? nameM[1].trim() : '',
      participants: parseParticipants(chunk),
      messages:     parseThreadMessages(chunk),
    };
  });
}

// ─── Instagram-specific parsers ───────────────────────────────────────────────

function extractFollowerList(html, itemLabel) {
  const re = new RegExp(`>${itemLabel}<div class="m"><div>([^<]+)`, 'g');
  const items = [];
  let m;
  while ((m = re.exec(html)) !== null) {
    const raw = m[1].trim();
    const nextPos = html.indexOf(`>${itemLabel}<`, m.index + 1);
    const block   = html.slice(m.index, nextPos === -1 ? html.length : nextPos);
    const tsM     = block.match(/>Timestamp<div class="m"><div>([^<]+)/);
    items.push({ raw, timestamp: tsM ? tsM[1].trim() : '' });
  }
  return items;
}

function extractPhotos(html) {
  const re = /<img\s+src="([^"]+)"/g;
  const photos = [];
  let m;
  while ((m = re.exec(html)) !== null) {
    const src = m[1];
    const afterImg = m.index + m[0].length;
    const nextImg  = html.indexOf('<img ', afterImg);
    const blockEnd = nextImg === -1 ? html.length : nextImg;
    const block    = html.slice(m.index, blockEnd);
    photos.push({
      src,
      taken:    block.match(/>Taken<div class="m"><div>([^<]+)/)?.[1]?.trim()             || '',
      id:       block.match(/>Id<div class="m"><div>([^<]+)/)?.[1]?.trim()                || '',
      status:   block.match(/>Status<div class="m"><div>([^<]+)/)?.[1]?.trim()            || '',
      privacy:  block.match(/>Privacy Setting<div class="m"><div>([^<]+)/)?.[1]?.trim()   || '',
      url:      block.match(/>Url<div class="m"><div>([^<]+)/)?.[1]?.trim()               || '',
      uploadIp: block.match(/>Upload Ip<div class="m"><div>([^<]+)/)?.[1]?.trim()         || '',
      source:   block.match(/>Source<div class="m"><div>([^<]+)/)?.[1]?.trim()            || '',
    });
  }
  return photos;
}

function extractVideos(html) {
  const re = /<source\s+src="([^"]+)"\s+type="video/g;
  const videos = [];
  let m;
  while ((m = re.exec(html)) !== null) {
    const src      = m[1];
    const afterVid = m.index + m[0].length;
    const nextVid  = html.indexOf('<source ', afterVid);
    // look back 400 chars for metadata that appears before <video>
    const blockStart = Math.max(0, m.index - 400);
    const blockEnd   = nextVid === -1 ? html.length : nextVid;
    const block      = html.slice(blockStart, blockEnd);
    videos.push({
      src,
      taken:    block.match(/>Taken<div class="m"><div>([^<]+)/)?.[1]?.trim()             || '',
      id:       block.match(/>Id<div class="m"><div>([^<]+)/)?.[1]?.trim()                || '',
      status:   block.match(/>Status<div class="m"><div>([^<]+)/)?.[1]?.trim()            || '',
      privacy:  block.match(/>Privacy Setting<div class="m"><div>([^<]+)/)?.[1]?.trim()   || '',
      url:      block.match(/>Url<div class="m"><div>([^<]+)/)?.[1]?.trim()               || '',
      uploadIp: block.match(/>Upload Ip<div class="m"><div>([^<]+)/)?.[1]?.trim()         || '',
    });
  }
  return videos;
}

function extractDevices(html) {
  const typeRe = />Type<div class="m"><div>([^<]+)/g;
  const positions = [];
  let m;
  while ((m = typeRe.exec(html)) !== null)
    positions.push({ type: m[1].trim(), index: m.index });
  return positions.map((pos, i) => {
    const blockEnd = i + 1 < positions.length ? positions[i + 1].index : html.length;
    const block    = html.slice(pos.index, blockEnd);
    const id       = block.match(/>Id<div class="m"><div>([^<]+)/)?.[1]?.trim()     || '';
    const active   = block.match(/>Active<div class="m"><div>([^<]+)/)?.[1]?.trim() || '';
    const userRe   = />User<div class="m"><div>([^<]+)/g;
    const users    = [];
    let um;
    while ((um = userRe.exec(block)) !== null) users.push(um[1].trim());
    return { type: pos.type, id, active, users };
  });
}

function extractComments(html) {
  const re = />Date Created<div class="m"><div>([^<]+)/g;
  const positions = [];
  let m;
  while ((m = re.exec(html)) !== null)
    positions.push({ dateCreated: m[1].trim(), index: m.index });
  return positions.map((pos, i) => {
    const blockStart = Math.max(0, pos.index - 600);
    const blockEnd   = i + 1 < positions.length ? positions[i + 1].index : html.length;
    const block      = html.slice(blockStart, blockEnd);
    return {
      dateCreated: pos.dateCreated,
      text:    block.match(/>Text<div class="m"><div>([^<]+)/)?.[1]?.trim()           || '',
      status:  block.match(/>Status<div class="m"><div>([^<]+)/)?.[1]?.trim()         || '',
      owner:   block.match(/>Media Owner<div class="m"><div>([^<]+)/)?.[1]?.trim()    || '',
      privacy: block.match(/>Privacy Setting<div class="m"><div>([^<]+)/)?.[1]?.trim()|| '',
    };
  });
}

function extractLikes(html) {
  const re = />Like Timestamp<div class="m"><div>([^<]+)/g;
  const items = [];
  let m;
  while ((m = re.exec(html)) !== null) {
    const likeTs     = m[1].trim();
    const blockStart = Math.max(0, m.index - 1000);
    const blockEnd   = m.index + m[0].length + 500;
    const block      = html.slice(blockStart, blockEnd);
    items.push({
      likeTimestamp: likeTs,
      author: block.match(/>Liked Post Author Vanity<div class="m"><div>([^<]+)/)?.[1]?.trim() || '',
      url:    block.match(/>Url<div class="m"><div>([^<]+)/)?.[1]?.trim()                      || '',
      status: block.match(/>Status<div class="m"><div>([^<]+)/)?.[1]?.trim()                   || '',
    });
  }
  return items;
}

// ─── Main parse ───────────────────────────────────────────────────────────────

export function parseInstagramReturn(html) {
  const raw      = sliceSections(html);
  const sections = {};

  for (const key of SECTION_KEYS) {
    const chunk = raw[key] || '';
    const count = countSection(key, chunk);
    const needsKv = count > 0
      && !MESSAGE_SECTIONS.has(key)
      && !IP_KEYS.has(key)
      && !MEDIA_SECTION_KEYS.has(key)
      && !FOLLOWER_SECTION_KEYS.has(key)
      && !COMMENT_SECTION_KEYS.has(key)
      && !LIKES_SECTION_KEYS.has(key)
      && !DEVICES_SECTION_KEYS.has(key);
    sections[key] = {
      label:              SECTION_LABELS[key],
      count,
      is_message_section: MESSAGE_SECTIONS.has(key),
      kv_pairs:           needsKv ? extractKvPairs(chunk) : [],
    };
  }

  const summary = {
    name:              extractName(raw['name'] || ''),
    vanity:            fieldValue(raw['vanity']            || '', 'Vanity Name'),
    about_me:          fieldValue(raw['about_me']          || '', 'About Me'),
    date_of_birth:     fieldValue(raw['date_of_birth']     || '', 'Date Of Birth'),
    gender:            fieldValue(raw['gender']            || '', 'Gender'),
    registration_date: fieldValue(raw['registration_date'] || '', 'Registration Date'),
    registration_ip:   fieldValue(raw['registration_ip']   || '', 'Registration Ip'),
    phone_numbers:     fieldValue(raw['phone_numbers']     || '', 'Phone Numbers'),
    emails:            fieldValue(raw['emails']            || '', 'Registered Email Addresses'),
    website:           fieldValue(raw['website']           || '', 'Website'),
    account_active:    fieldValue(raw['account_end_date']  || '', 'Account Still Active'),
  };

  const subject_id =
    fieldValue(raw['request_parameters'] || '', 'Account Identifier') ||
    fieldValue(raw['request_parameters'] || '', 'Target');

  const message_threads = {};
  for (const key of MESSAGE_SECTION_KEYS) {
    if (sections[key]?.count > 0)
      message_threads[key] = parseMessageThreads(raw[key] || '');
  }

  const ip_records = extractIpRecords(raw['ip_addresses'] || '');
  const ipCounts   = {};
  for (const r of ip_records)
    if (r.ip) ipCounts[r.ip] = (ipCounts[r.ip] || 0) + 1;
  const top_ips = Object.entries(ipCounts)
    .sort((a, b) => b[1] - a[1]).slice(0, 10)
    .map(([ip, count]) => ({ ip, count }));

  const followers  = extractFollowerList(raw['followers']               || '', 'Follower');
  const following  = extractFollowerList(raw['following']               || '', 'Following');
  const followReqs = extractFollowerList(raw['incoming_follow_requests']|| '', 'Follow Request');
  const photos     = extractPhotos(raw['photos'] || '');
  const profilePic = extractPhotos(raw['profile_picture'] || '');
  const videos     = extractVideos(raw['videos']             || '');
  const liveVideos = extractVideos(raw['live_videos']        || '');
  const archivedLive = extractVideos(raw['archived_live_videos'] || '');
  const devices    = extractDevices(raw['devices']   || '');
  const comments   = extractComments(raw['comments'] || '');
  const commentLikes = extractComments(raw['comment_likes'] || '');
  const likes      = extractLikes(raw['likes'] || '');

  // Override counts now that we have accurate parsed lengths
  sections['followers'].count              = followers.length;
  sections['following'].count              = following.length;
  sections['incoming_follow_requests'].count = followReqs.length;
  sections['photos'].count                 = photos.length;
  sections['profile_picture'].count        = profilePic.length;
  sections['videos'].count                 = videos.length;
  sections['live_videos'].count            = liveVideos.length;
  sections['archived_live_videos'].count   = archivedLive.length;
  sections['devices'].count                = devices.length;
  sections['comments'].count               = comments.length;
  sections['comment_likes'].count          = commentLikes.length;
  sections['likes'].count                  = likes.length;

  return {
    subject_id, summary, sections, message_threads,
    ip_records, top_ips,
    followers, following, followReqs,
    photos, profilePic, videos, liveVideos, archivedLive,
    devices, comments, commentLikes, likes,
  };
}
